import { useMemo } from 'react';
import { css } from '@emotion/react';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { useCallIntelFeatureAccess } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { allowedNotificationTypes, combinedTagNotificationType } from '@frontend/settings-routing';
import { usePreference } from '@frontend/user-preferences';
import { theme } from '@frontend/theme';
import { ChecklistField, useFormField, Text, Button, FieldChangeEvent, Image } from '@frontend/design-system';
import NotificationCenterPreview from '../../images/notification-center-preview.svg';

export const GroupedFilters = ({ closePopOver }: { closePopOver: () => void }) => {
  const { t } = useTranslation();
  const TAGS = 'TAGS';
  /* Adding this combined checkbox for all types of Tags */
  const options = [
    ...allowedNotificationTypes.map((type) => ({ value: type.firestoreType, label: type.displayName })),
    { value: TAGS as NotificationType, label: 'Tags' },
  ];
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isTaskCenterEnabled = getFeatureFlagValue('new-task-center-experience');

  const { shouldHideFeature: shouldHideCallIntelFeature } = useCallIntelFeatureAccess({
    permissions: [Permission.CALL_CLASSIFICATION_READ],
    featureFlagKey: 'enable-unhappy-call-notification-backend',
  });

  const hideAnyFilters = [
    NotificationType.CHAT_MESSAGE,
    NotificationType.TASK_CENTER_AI_AUTO_ASSIGN,
    !isTaskCenterEnabled && NotificationType.TASK,
    shouldHideCallIntelFeature && NotificationType.CALL_INTELLIGENCE_UNHAPPY_CALL,
  ];
  const TAGS_NOTIFICATION_TYPES = combinedTagNotificationType.firestoreType;
  const [forYouNotificationFilters, setForYouNotificationFilters] = usePreference('ForYouNotificationFilters');

  const checklistField = useFormField(
    {
      type: 'checklist',
      value: forYouNotificationFilters?.filter(
        (val: string) => !TAGS_NOTIFICATION_TYPES.includes(val as NotificationType)
      ),
    },
    [forYouNotificationFilters]
  );

  const handleChecklistChange = (e: FieldChangeEvent<HTMLInputElement> | undefined) => {
    if (!e) return;

    checklistField.onChange(e);
  };

  const handleSave = () => {
    if (checklistField.value.includes(TAGS)) {
      checklistField.value = [...checklistField.value, ...TAGS_NOTIFICATION_TYPES];
    } else
      checklistField.value = checklistField.value.filter(
        (val) => !TAGS_NOTIFICATION_TYPES.includes(val as NotificationType)
      );
    setForYouNotificationFilters(checklistField.value);
    closePopOver();
  };

  const handleSelectAllToggle = () => {
    const isAnySelected = checklistField.value.length > 0;
    const newValue = isAnySelected
      ? []
      : [...options.filter((notification) => !hideAnyFilters.includes(notification.value)).map((o) => o.value)];
    checklistField.onChange({
      value: newValue,
      name: 'checklistField',
    });
  };

  const sortedOptions = useMemo(() => {
    return options
      .filter((notification) => !hideAnyFilters.includes(notification.value))
      .sort((a, b) => {
        const aSelected = checklistField.value.includes(a.value);
        const bSelected = checklistField.value.includes(b.value);
        if (aSelected === bSelected) return 0;
        return aSelected ? -1 : 1; // selected ones on the top
      });
  }, []);

  return (
    <div css={{ width: 350 }}>
      <div
        css={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: theme.spacing(8.75),
          padding: `${theme.spacing(3)} ${theme.spacing(2)} ${theme.spacing(2)}`,
          borderBottom: `1px solid ${theme.colors.neutral20}`,
        }}
      >
        <Text
          onClick={handleSelectAllToggle}
          css={{
            minWidth: 85,
            cursor: 'pointer',
            color: theme.colors.primary50,
            whiteSpace: 'nowrap',
            fontSize: theme.spacing(1.75),
            fontWeight: theme.font.weight.bold,
            marginBottom: `-${theme.spacing(0.25)}`,
          }}
          data-trackingid={
            checklistField.value.length > 0 ? `deselect-all-selected-filters` : 'select-all-selected-filters'
          }
        >
          {checklistField.value.length > 0 ? `Deselect (${checklistField.value.length})` : t('Select All')}
        </Text>
        <div>
          <Image
            src={NotificationCenterPreview}
            css={css`
              height: 100%;
              width: 100%;
              box-shadow: 0px 1px 14.6px 4px #00000021;
            `}
            alt='notification-center-preview'
            width={109}
            height={65}
            loading='lazy'
          />
          <Text css={{ marginTop: theme.spacing(1), fontSize: theme.spacing(1.5), fontWeight: theme.font.weight.bold }}>
            {t('Notification Center')}
          </Text>
        </div>
      </div>

      <ChecklistField
        {...checklistField}
        onChange={handleChecklistChange}
        name='checklistField'
        label=''
        showLabel={false}
      >
        {sortedOptions.map((option, index) => (
          <div
            key={index}
            css={{
              display: 'flex',
              padding: `${theme.spacing(1.75)} ${theme.spacing(2)}`,
              borderBottom: `1px solid ${theme.colors.neutral20}`,
              gap: 100,
            }}
          >
            <Text
              css={{
                width: '100px',
                whiteSpace: 'nowrap',
                fontWeight: checklistField.value.includes(option.value)
                  ? theme.font.weight.bold
                  : theme.font.weight.regular,
              }}
            >
              {option.label}
            </Text>
            <ChecklistField.Option key={option.value} name={option.value} css={{ marginBottom: '0' }}>
              <Text css={{ display: 'none' }}>{option.label}</Text>
            </ChecklistField.Option>
          </div>
        ))}
      </ChecklistField>
      <div
        css={{
          padding: theme.spacing(2),
          zIndex: 1,
          position: 'sticky',
          bottom: '0',
          background: 'white',
          borderTop: `1px solid ${theme.colors.neutral20}`,
        }}
      >
        <Button
          onClick={handleSave}
          css={{ width: '100%' }}
          variant='primary'
          size='large'
          type='submit'
          trackingId='save-changes-show-me-filters'
        >
          {' '}
          {t('Save changes')}
        </Button>
      </div>
    </div>
  );
};
